import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, ArrowRightLeft, LogOut, LifeBuoy, Shield, Database, FileText } from 'lucide-react';
import { getPersonaFromPath } from '../../../../utils/accessControl';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/assist/ui/dropdown-menu';
import { Button } from '@/components/assist/ui/button';
import { Avatar, AvatarFallback } from '@/components/assist/ui/avatar';
import { useProfile } from '@/contexts/ProfileContext';
import { useSupportDrawer } from '@/contexts/SupportDrawerContext';

function getInitials(
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null,
): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) return firstName[0].toUpperCase();
  if (email) return email[0].toUpperCase();
  return 'U';
}

export function UserProfileDropdown() {
  const { t } = useTranslation('assist');
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useProfile();
  const { openDrawer } = useSupportDrawer();
  const initials = getInitials(
    profile?.first_name,
    profile?.last_name,
    profile?.primary_email,
  );

  const displayName = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(' ') || profile?.primary_email || '';

  const session = JSON.parse(localStorage.getItem('session') || '{}');
  const accessibleLocations: Array<{ persona: string; path: string }> = session.accessibleLocations || [];
  const currentPersona = getPersonaFromPath(location.pathname);

  const PERSONA_LABEL_KEYS: Record<string, string> = {
    'self-care': 'header.goToSelfCare',
    insights: 'header.goToInsights',
    // 'admin' deliberately omitted — the /admin section is hidden (EL-502), so
    // the BFF no longer emits an admin entry in accessibleLocations.
  };

  // Map 'assist' persona (internal name) to 'insights' (URL/display name)
  const currentPersonaKey = currentPersona === 'assist' ? 'insights' : currentPersona;
  // The Account items (Security, Data, Audit Logs) used to live in a dedicated
  // "Account" section of the insights sidebar; they now live here. They point
  // at insights-only routes, so only surface them in the insights persona.
  const isInsights = currentPersona === 'assist';
  const otherLocations = accessibleLocations.filter(
    (loc) => loc.persona !== currentPersonaKey
  );

  const handleLogout = async () => {
    try {
      await fetch('/api/session/logout', {
        method: 'POST',
        credentials: 'include',
      });
      localStorage.removeItem('elli-current-tenant-id');
      localStorage.removeItem('elli-user-profile');
      localStorage.removeItem('elli-sidebar-collapsed');
    } catch {
      // Ignore errors
    }
    window.location.href = '/';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-xs bg-primary text-primary-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 bg-popover" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            {profile?.primary_email && (
              <p className="text-xs leading-none text-muted-foreground">
                {profile.primary_email}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => {
            const persona = getPersonaFromPath(location.pathname);
            navigate(`/${persona === 'assist' ? 'insights' : (persona || 'self-care')}/profile`);
          }}
        >
          <User className="mr-2 h-4 w-4" />
          {t('header.myProfile')}
        </DropdownMenuItem>
        {isInsights && (
          <>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => navigate('/insights/profile?tab=security')}
            >
              <Shield className="mr-2 h-4 w-4" />
              {t('sidebar.security')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => navigate('/insights/admin')}
            >
              <Database className="mr-2 h-4 w-4" />
              {t('sidebar.data')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => navigate('/insights/audit-logs')}
            >
              <FileText className="mr-2 h-4 w-4" />
              {t('sidebar.auditLogs')}
            </DropdownMenuItem>
          </>
        )}
        {otherLocations.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {otherLocations.map((loc) => (
              <DropdownMenuItem
                key={loc.persona}
                className="cursor-pointer"
                onClick={() => navigate(loc.path)}
              >
                <ArrowRightLeft className="mr-2 h-4 w-4" />
                {t(PERSONA_LABEL_KEYS[loc.persona] || loc.persona)}
              </DropdownMenuItem>
            ))}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => openDrawer('list')}
        >
          <LifeBuoy className="mr-2 h-4 w-4" />
          {t('header.support')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          {t('header.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
